import React, { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CallPreview, { CallSettings } from './CallPreview';
import { Container, Spinner } from 'react-bootstrap';
import { fetchFromRestAPI, useAuthFetchFromRestAPI } from '../../util/api';
import { AUTH0_ORGANIZATION_ID } from '../../config';
import { JoinCallDto } from '../../../../shared/dtos/joinCall.dto';
import { JoinCallRequest } from '../../../../shared/requests/joinCall.request';
import { NavigateFunction, useNavigate, useParams } from 'react-router';
import { Call, StreamVideoClient, StreamVideoParticipant } from '@stream-io/video-react-sdk';
import CallSession from './CallSession';
import CallView from './CallView';
import CallWaitingRoom from './CallWaitingRoom';
import { useAuth0 } from '@auth0/auth0-react';
import PrivacyModal from '../PrivacyModal';
import i18next from 'i18next';

type CallPageState = 'preview' | 'connecting' | 'waitingRoom' | 'inCall';

const CallPage = (): ReactElement => {
  const navigate: NavigateFunction = useNavigate();
  const { roomCode: rawRoomCode } = useParams<{ roomCode: string }>();

  const [pageState, setPageState] = useState<CallPageState>('preview');
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinData, setJoinData] = useState<JoinCallDto | null>(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const [streamClient, setStreamClient] = useState<StreamVideoClient | null>(null);
  const [streamCall, setStreamCall] = useState<Call | null>(null);

  const [callJoinedAt, setCallJoinedAt] = useState<Date | null>(null);

  const clientRef = useRef<StreamVideoClient | null>(null);
  const callRef = useRef<Call | null>(null);

  const [pendingJoinData, setPendingJoinData] = useState<JoinCallDto | null>(null);
  const [pendingCallSettings, setPendingCallSettings] = useState<CallSettings | null>(null);

  const { isAuthenticated } = useAuth0();
  const { authFetch } = useAuthFetchFromRestAPI();

  const roomCode: string = useMemo(() => {
    return (rawRoomCode ?? '').trim().toUpperCase();
  }, [rawRoomCode]);

  const teardown = useCallback(async (): Promise<void> => {
    const call: Call | null = callRef.current;
    const client: StreamVideoClient | null = clientRef.current;

    callRef.current = null;
    clientRef.current = null;

    if (call) {
      await call.camera.disable().catch(() => undefined);
      await call.microphone.disable().catch(() => undefined);
      await call.leave().catch(() => undefined);
    }

    if (client) {
      await client.disconnectUser().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    return (): void => {
      void teardown();
    };
  }, [teardown]);

  const connectToCall = async (result: JoinCallDto, settings: CallSettings): Promise<void> => {
    setPageState('connecting');

    const user = {
      id: result.userId,
      name: result.callName,
      image: result.callImage,
    };

    const client: StreamVideoClient = StreamVideoClient.getOrCreateInstance({
      apiKey: result.apiKey,
      user,
      token: result.token,
    });

    const call: Call = client.call(result.callType, result.callId);

    if (settings.selectedCameraId) {
      await call.camera.select(settings.selectedCameraId).catch(() => undefined);
    }

    if (settings.selectedMicrophoneId) {
      await call.microphone.select(settings.selectedMicrophoneId).catch(() => undefined);
    }

    if (settings.cameraEnabled) {
      await call.camera.enable().catch(() => undefined);
    } else {
      await call.camera.disable().catch(() => undefined);
    }

    if (settings.microphoneEnabled) {
      await call.microphone.enable().catch(() => undefined);
    } else {
      await call.microphone.disable().catch(() => undefined);
    }

    await call.join();
    
    const startedAt: Date = call.state.startedAt ? new Date(call.state.startedAt) : new Date();

    clientRef.current = client;
    callRef.current = call;
    setStreamClient(client);
    setStreamCall(call);
    setJoinData(result);
    setCallJoinedAt(startedAt);
    setPageState(result.waitingRoom ? 'waitingRoom' : 'inCall');
  };

  const checkPrivacy = async (): Promise<boolean> => {
    try {
      return await fetchFromRestAPI(
        `/api/v1/privacy/${encodeURIComponent(roomCode)}?tenant=${AUTH0_ORGANIZATION_ID}`
      );
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const handleJoin = async (settings: CallSettings): Promise<void> => {
    try {
      if (!roomCode) {
        setJoinError(i18next.t('callPage.noRoomCodeProvided'));
        return;
      }

      setIsJoining(true);
      setJoinError(null);

      const query: URLSearchParams = new URLSearchParams({ tenant: AUTH0_ORGANIZATION_ID });
      const body: JoinCallRequest = { platform: 'web' };
      const url: string = `/api/v1/call/join/${encodeURIComponent(roomCode)}?${query.toString()}`;

      const result: JoinCallDto = isAuthenticated
        ? await authFetch(url, { method: 'POST', body })
        : await fetchFromRestAPI(url, { method: 'POST', body });

      if (result.authMode === 'room_code') {
        const privacyAccepted: boolean = await checkPrivacy();

        if (!privacyAccepted) {
          setPendingJoinData(result);
          setPendingCallSettings(settings);
          setShowPrivacyModal(true);
          return;
        }
      }

      await connectToCall(result, settings);
    } catch (error) {
      console.error(error);
      setJoinError(i18next.t('callPage.entryRoomError'));
      setPageState('preview');
    } finally {
      setIsJoining(false);
    }
  };

  const handlePrivacyAccept = async (): Promise<void> => {
    try {
      if (!pendingJoinData || !pendingCallSettings) {
        setShowPrivacyModal(false);
        return;
      }

      setIsJoining(true);
      setJoinError(null);

      await fetchFromRestAPI(
        `/api/v1/privacy/${encodeURIComponent(roomCode)}?tenant=${AUTH0_ORGANIZATION_ID}`,
        { method: 'POST' }
      );

      setShowPrivacyModal(false);
      await connectToCall(pendingJoinData, pendingCallSettings);

      setPendingJoinData(null);
      setPendingCallSettings(null);
    } catch (error) {
      console.error(error);
      setJoinError(i18next.t('callPage.privacyPolicySaveError'));
      setPageState('preview');
    } finally {
      setIsJoining(false);
    }
  };

  const handlePrivacyClose = (): void => {
    setShowPrivacyModal(false);
    setPendingJoinData(null);
    setPendingCallSettings(null);
    setIsJoining(false);
  };

  const handleLeaveWaitingRoom = async (): Promise<void> => {
    await teardown();
    setStreamClient(null);
    setStreamCall(null);
    setJoinData(null);
    setCallJoinedAt(null);
    setJoinError(null);
    setPageState('preview');
  };

  const handleLeaveCall = (): void => {
    navigate(`/call-feedback/${roomCode}/${joinData?.role === 'therapist' ? 1 : 0}`);
  };

  const handleAdmitted = (): void => {
    setPageState('inCall');
  };

  if (!roomCode) {
    return (
      <Container fluid className="py-4 py-md-5">
        <div className="d-flex justify-content-center">
          <div>{i18next.t('callPage.noRoomCodeInURL')}.</div>
        </div>
      </Container>
    );
  }

  const isInCallState: boolean = pageState === 'inCall';

  return (
    <div
      className="d-flex flex-column flex-grow-1 w-100"
      style={{
        padding: isInCallState ? '0' : '32px 12px 48px',
        ...(isInCallState ? { overflow: 'hidden', minHeight: 0 } : {}),
      }}
    >
      {pageState === 'preview' && (
        <Container fluid className="d-flex flex-grow-1 align-items-center justify-content-center">
          <div className="d-flex justify-content-center w-100">
            <CallPreview
              roomCode={roomCode}
              mode="preview"
              isSubmitting={isJoining}
              error={joinError}
              onJoin={handleJoin}
              onBack={() => navigate(-1)}
            />
          </div>
        </Container>
      )}

      {pageState === 'connecting' && (
        <Container fluid className="d-flex flex-grow-1 align-items-center justify-content-center">
          <div className="d-flex flex-column align-items-center text-white">
            <Spinner animation="border" className="mb-3" />
            <span className="small opacity-75">{i18next.t('callPage.connecting')}</span>
          </div>
        </Container>
      )}

      {streamClient && streamCall && (pageState === 'waitingRoom' || pageState === 'inCall') && (
        <CallSession client={streamClient} call={streamCall}>
          {pageState === 'waitingRoom' && (
            <CallWaitingRoomContainer
              call={streamCall}
              roomCode={roomCode}
              onLeave={() => void handleLeaveWaitingRoom()}
              onAdmitted={handleAdmitted}
            />
          )}

          {pageState === 'inCall' && joinData && callJoinedAt && (
            <div className="d-flex flex-grow-1 w-100" style={{ minHeight: 0, overflow: 'hidden' }}>
              <CallView
                role={joinData.role}
                roomCode={roomCode}
                callStartedAt={callJoinedAt}
                onLeaveCall={handleLeaveCall}
              />
            </div>
          )}

          <CallEndedListener call={streamCall} onCallEnded={handleLeaveCall} />
        </CallSession>
      )}

      <PrivacyModal
        show={showPrivacyModal}
        onPositive={() => void handlePrivacyAccept()}
        onNegative={handlePrivacyClose}
        onSupport={handlePrivacyClose}
      />
    </div>
  );
};


type CallWaitingRoomContainerProps = {
  call: Call;
  roomCode: string;
  onLeave: () => void;
  onAdmitted: () => void;
};

const CallWaitingRoomContainer: FC<CallWaitingRoomContainerProps> = ({
  call,
  roomCode,
  onLeave,
  onAdmitted,
}: CallWaitingRoomContainerProps): ReactElement => {
  useEffect(() => {
    const unsubscribe: () => void = call.on('call.session_participant_joined', () => {
      const remoteParticipants: StreamVideoParticipant[] = call.state.participants.filter((p: StreamVideoParticipant): boolean => !p.isLocalParticipant);
      if (remoteParticipants.length > 0) {
        onAdmitted();
      }
    });

    return (): void => {
      unsubscribe();
    };
  }, [call, onAdmitted]);

  return (
    <Container fluid className="d-flex flex-grow-1 align-items-center justify-content-center">
      <div className="d-flex justify-content-center w-100">
        <CallWaitingRoom roomCode={roomCode} onLeave={onLeave} />
      </div>
    </Container>
  );
};


type CallEndedListenerProps = {
  call: Call;
  onCallEnded: () => void;
};

const CallEndedListener: FC<CallEndedListenerProps> = ({ call, onCallEnded }: CallEndedListenerProps): null => {
  const callbackRef = useRef(onCallEnded);
  callbackRef.current = onCallEnded;

  useEffect(() => {
    const unsubscribe: () => void = call.on('call.ended', () => {
      callbackRef.current();
    });
    return (): void => {
      unsubscribe();
    };
  }, [call]);

  return null;
};

export default CallPage;